import { useEffect, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Anchor, Box, Center, Loader, Stack, Text } from '@mantine/core';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import StatBand, { type StatItem } from '@/design/StatBand';
import { PolicyDetailViewModel } from './PolicyDetailViewModel';
import PolicyEvidenceTable from './PolicyEvidenceTable';
import { formatPct } from './PolicyFormatting';
import { STATUS_VISUAL } from './PolicyStatusVisual';

function NotFound(): ReactElement {
    return (
        <Box pb="xl">
            <Stack gap="sm" align="flex-start">
                <Text fw={700}>Policy not found</Text>
                <Text c="dimmed" size="sm">This governance policy no longer exists or was never created.</Text>
                <Anchor component={Link} to="/governance" size="sm" fw={600}>← Back to Governance</Anchor>
            </Stack>
        </Box>
    );
}

/**
 * The policy detail drill-in: the turns classified under one governance policy,
 * each showing the verdict its output type produces. The policy is live; the
 * classified turns are illustrative until per-classification telemetry exists.
 */
export default function PolicyDetailPage(): ReactElement {
    const { policyId } = useParams<{ policyId: string }>();
    const vm = useViewModel(PolicyDetailViewModel);

    useEffect(() => {
        if (policyId !== undefined) void vm.initialize(policyId);
    }, [vm, policyId]);

    if (!vm.loaded) {
        return (
            <Center py="xl">
                <Loader size="sm" />
            </Center>
        );
    }

    if (vm.notFound || vm.view === null) return <NotFound />;

    const { view } = vm;
    const Icon = view.icon;
    const visual = STATUS_VISUAL[view.status];

    const stats: StatItem[] = [
        { label: 'Classified prompts', value: view.turns.toLocaleString(), hint: 'selected range' },
        { label: 'Flagged prompts', value: view.concernTurns.toLocaleString(), hint: visual.label },
        { label: 'Top verdict', value: view.topValue ?? '—' },
        { label: 'Avg confidence', value: formatPct(view.avgConfidence) },
    ];

    return (
        <Box pb="xl">
            <SurfaceBreadcrumbRow items={[{ label: 'Governance', to: '/governance' }, { label: view.policy.name }]} />

            <PageHeaderComponent
                eyebrow="Policy · classified prompts"
                leading={
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            background: visual.soft,
                            border: `1px solid ${visual.border}`,
                            borderRadius: 'var(--bm-radius)',
                        }}
                    >
                        <Icon size={20} color={visual.solid} />
                    </Box>
                }
                title={view.policy.name}
                subtitle={view.policy.description || undefined}
            />

            <Stack gap="md" mt="md">
                <StatBand stats={stats} />

                {vm.items.length === 0 ?
                    <Text c="dimmed" size="sm">No prompts have been classified under this policy in the current window.</Text>
                :   <PolicyEvidenceTable view={view} items={vm.items} persistenceKey={`policy-evidence:${policyId ?? 'unknown'}`} />
                }
            </Stack>
        </Box>
    );
}
